import { createContext, useContext, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import Seo from "@/components/common/Seo";

const ShareCtx = createContext(null);
export const useShare = () => useContext(ShareCtx);

const STEPS = ["/share", "/share/about", "/share/where", "/share/details", "/share/story", "/share/thanks"];

export default function ShareFlow() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const idx = STEPS.indexOf(pathname) === -1 ? 0 : STEPS.indexOf(pathname);

  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [centres, setCentres] = useState([]);
  const [organStates, setOrganStates] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    journey_type: "", organ: "", state_code: "", city: "", hospital_id: "", hospital_name_text: "",
    journey_year: "", language: "English", story_text: "", lessons: "",
    audio_url: null, audio_duration: null, images: [],
    identity_preference: "", contributor_first_name: "", contributor_last_name: "", contributor_email: "", contributor_phone: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { api.get("/states").then((r) => setStates(r.data)); }, []);
  useEffect(() => {
    if (!form.state_code) return;
    api.get(`/cities?state=${form.state_code}`).then((r) => setCities(r.data));
    const organParam = form.organ && form.organ !== "other" ? `&organ=${form.organ}` : "";
    api.get(`/centres?state=${form.state_code}${organParam}`).then((r) => setCentres(r.data));
  }, [form.state_code, form.organ]);

  useEffect(() => {
    if (!form.organ || form.organ === "other") { setOrganStates(null); return; }
    api.get(`/centres?organ=${form.organ}`).then((r) => {
      setOrganStates(new Set(r.data.map((c) => c.state_code)));
    });
  }, [form.organ]);

  const identityOk =
    !!form.identity_preference && !!form.contributor_first_name.trim() &&
    !!form.contributor_last_name.trim() && !!form.contributor_phone.trim();

  const canNext = {
    1: !!form.journey_type && !!form.organ,
    2: !!form.state_code && ((!!form.hospital_id && form.hospital_id !== "__not_listed") || (form.hospital_id === "__not_listed" && !!form.hospital_name_text.trim())),
    3: !!form.journey_year,
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const isNotListed = form.hospital_id === "__not_listed";
      const payload = {
        ...form,
        journey_year: form.journey_year ? Number(form.journey_year) : null,
        hospital_id: isNotListed ? null : (form.hospital_id || null),
        hospital_name_text: isNotListed ? (form.hospital_name_text.trim() || null) : null,
      };
      const { data } = await api.post("/journeys", payload);
      toast.success("Thank you. Your journey has been received for review.");
      navigate("/share/thanks");
      return data;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ShareCtx.Provider value={{ form, set, states, cities, centres, organStates, submit, submitting, identityOk }}>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Seo title="Share Your Journey — Transplant Story" description="Tell us what happened. Share your transplant journey by voice or in writing in two to three minutes." />
        {idx >= 1 && idx <= 4 && (
          <div className="flex items-center gap-2 mb-10">
            {[1, 2, 3, 4].map((n) => (
              <div key={n} className={`h-1 flex-1 rounded-full transition-colors ${n <= idx ? "bg-rust" : "bg-neutral-200"}`} />
            ))}
          </div>
        )}
        <Outlet />
        {idx >= 1 && idx <= 4 && (
          <div className="flex justify-between mt-12">
            <button data-testid="share-back" onClick={() => navigate(STEPS[idx - 1])}
              className="text-sm text-neutral-500 hover:text-ink transition-colors">← Back</button>
            {idx < 4 ? (
              <button data-testid="share-next" disabled={!canNext[idx]} onClick={() => navigate(STEPS[idx + 1])}
                className="bg-ink text-white px-7 py-3 text-sm font-medium disabled:opacity-30 hover:bg-rust transition-colors">
                Continue
              </button>
            ) : (
              <button data-testid="share-submit" disabled={submitting || (!form.story_text && !form.audio_url) || !identityOk} onClick={submit}
                className="bg-rust text-white px-7 py-3 text-sm font-medium disabled:opacity-30 hover:bg-rust-hover transition-colors">
                {submitting ? "Submitting…" : "Submit my journey"}
              </button>
            )}
          </div>
        )}
      </div>
    </ShareCtx.Provider>
  );
}
